
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { UserProfile } from "./types";
import { UserFilters, UserSortOption } from "./UserFilters";
import type { Database } from "@/integrations/supabase/types";
import { usePermissions } from "@/hooks/use-permissions";

type UserRole = Database['public']['Enums']['user_role'];

interface UseFetchUsersProps {
  searchQuery: string;
  filters: UserFilters;
  sortOption: UserSortOption;
  pagination: {
    currentPage: number;
    itemsPerPage: number;
  };
}

export function useFetchUsers({ 
  searchQuery, 
  filters, 
  sortOption, 
  pagination 
}: UseFetchUsersProps) {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const { isSuperAdmin, getAssignedCountries } = usePermissions();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      let query = supabase.from("profiles").select("*", { count: 'exact' });
      
      // Apply country access restrictions for non-super admins
      if (!isSuperAdmin()) {
        const assignedCountries = getAssignedCountries();
        if (assignedCountries.length > 0) {
          query = query.in('country_code', assignedCountries);
        } else {
          // No assigned countries - return empty result
          setUsers([]);
          setTotalUsers(0);
          return;
        }
      }
      
      // Apply search filter
      if (searchQuery) {
        query = query.or(`name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`);
      }
      
      // Apply role filter
      if (filters.role !== 'all') {
        query = query.eq('role', filters.role as UserRole);
      }
      
      // Apply verified filter
      if (filters.verified !== 'all') {
        query = query.eq('verified', filters.verified === 'verified');
      }
      
      // Apply country filter (additional filter on top of access restrictions)
      if (filters.country !== 'all') {
        query = query.eq('country_code', filters.country);
      }
      
      // Apply sorting
      switch (sortOption) {
        case 'newest':
          query = query.order('created_at', { ascending: false });
          break;
        case 'oldest':
          query = query.order('created_at', { ascending: true });
          break;
        case 'name_asc':
          query = query.order('name', { ascending: true });
          break;
        case 'name_desc':
          query = query.order('name', { ascending: false });
          break;
      }
      
      // Apply pagination
      const start = (pagination.currentPage - 1) * pagination.itemsPerPage;
      const end = start + pagination.itemsPerPage - 1;
      query = query.range(start, end);
      
      const { data, error, count } = await query;
      
      if (error) throw error;
      
      setUsers(data || []);
      setTotalUsers(count || 0);
    } catch (error: any) {
      console.error("Error fetching users:", error.message);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchQuery, filters, sortOption, pagination.currentPage, pagination.itemsPerPage]);

  // Calculate paginated users
  const paginatedUsers = users;

  return { 
    users,
    setUsers,
    paginatedUsers, 
    totalUsers, 
    loading, 
    fetchUsers,
    refetch: fetchUsers 
  };
}
